import type { AwgSize, ConductorMaterial, TempRating } from "../types.ts";

/**
 * NEC 2023 Table 310.16 — Ampacities of insulated conductors rated up to
 * 2000V, not more than three current-carrying conductors in a raceway/cable,
 * based on an ambient temperature of 30°C (86°F).
 *
 * Columns: 60°C (e.g. TW), 75°C (e.g. THW/THWN), 90°C (e.g. THHN/THWN-2/XHHW-2).
 *
 * `null` = size not produced / not listed for that material (e.g. 14 AWG Al).
 * Values pinned by golden tests.
 */
type AmpacityRow = Record<TempRating, number | null>;

export const TABLE_310_16: Record<ConductorMaterial, Record<AwgSize, AmpacityRow>> = {
  copper: {
    "14": { "60C": 15, "75C": 20, "90C": 25 },
    "12": { "60C": 20, "75C": 25, "90C": 30 },
    "10": { "60C": 30, "75C": 35, "90C": 40 },
    "8": { "60C": 40, "75C": 50, "90C": 55 },
    "6": { "60C": 55, "75C": 65, "90C": 75 },
    "4": { "60C": 70, "75C": 85, "90C": 95 },
    "3": { "60C": 85, "75C": 100, "90C": 115 },
    "2": { "60C": 95, "75C": 115, "90C": 130 },
    "1": { "60C": 110, "75C": 130, "90C": 145 },
    "1/0": { "60C": 125, "75C": 150, "90C": 170 },
    "2/0": { "60C": 145, "75C": 175, "90C": 195 },
    "3/0": { "60C": 165, "75C": 200, "90C": 225 },
    "4/0": { "60C": 195, "75C": 230, "90C": 260 },
    "250": { "60C": 215, "75C": 255, "90C": 290 },
    "300": { "60C": 240, "75C": 285, "90C": 320 },
    "350": { "60C": 260, "75C": 310, "90C": 350 },
    "400": { "60C": 280, "75C": 335, "90C": 380 },
    "500": { "60C": 320, "75C": 380, "90C": 430 },
  },
  aluminum: {
    "14": { "60C": null, "75C": null, "90C": null },
    "12": { "60C": 15, "75C": 20, "90C": 25 },
    "10": { "60C": 25, "75C": 30, "90C": 35 },
    "8": { "60C": 35, "75C": 40, "90C": 45 },
    "6": { "60C": 40, "75C": 50, "90C": 55 },
    "4": { "60C": 55, "75C": 65, "90C": 75 },
    "3": { "60C": 65, "75C": 75, "90C": 85 },
    "2": { "60C": 75, "75C": 90, "90C": 100 },
    "1": { "60C": 85, "75C": 100, "90C": 115 },
    "1/0": { "60C": 100, "75C": 120, "90C": 135 },
    "2/0": { "60C": 115, "75C": 135, "90C": 150 },
    "3/0": { "60C": 130, "75C": 155, "90C": 175 },
    "4/0": { "60C": 150, "75C": 180, "90C": 205 },
    "250": { "60C": 170, "75C": 205, "90C": 230 },
    "300": { "60C": 195, "75C": 230, "90C": 260 },
    "350": { "60C": 210, "75C": 250, "90C": 280 },
    "400": { "60C": 225, "75C": 270, "90C": 305 },
    "500": { "60C": 260, "75C": 310, "90C": 350 },
  },
};

/**
 * NEC 2023 Table 310.15(B)(1) — ambient temperature correction factors.
 * Keyed by temperature-rating column; each entry is a list of
 * { maxAmbientC, factor } bands sorted ascending. A band applies when the
 * ambient temperature is <= maxAmbientC (and above the previous band's max).
 * `factor` is the multiplier applied to the Table 310.16 ampacity.
 */
type CorrectionBand = { maxAmbientC: number; factor: number };

export const TEMP_CORRECTION_310_15_B_1: Record<TempRating, CorrectionBand[]> = {
  "60C": [
    { maxAmbientC: 10, factor: 1.29 },
    { maxAmbientC: 15, factor: 1.22 },
    { maxAmbientC: 20, factor: 1.15 },
    { maxAmbientC: 25, factor: 1.08 },
    { maxAmbientC: 30, factor: 1.0 },
    { maxAmbientC: 35, factor: 0.91 },
    { maxAmbientC: 40, factor: 0.82 },
    { maxAmbientC: 45, factor: 0.71 },
    { maxAmbientC: 50, factor: 0.58 },
    { maxAmbientC: 55, factor: 0.41 },
  ],
  "75C": [
    { maxAmbientC: 10, factor: 1.2 },
    { maxAmbientC: 15, factor: 1.15 },
    { maxAmbientC: 20, factor: 1.11 },
    { maxAmbientC: 25, factor: 1.05 },
    { maxAmbientC: 30, factor: 1.0 },
    { maxAmbientC: 35, factor: 0.94 },
    { maxAmbientC: 40, factor: 0.88 },
    { maxAmbientC: 45, factor: 0.82 },
    { maxAmbientC: 50, factor: 0.75 },
    { maxAmbientC: 55, factor: 0.67 },
    { maxAmbientC: 60, factor: 0.58 },
    { maxAmbientC: 65, factor: 0.47 },
    { maxAmbientC: 70, factor: 0.33 },
  ],
  "90C": [
    { maxAmbientC: 10, factor: 1.15 },
    { maxAmbientC: 15, factor: 1.12 },
    { maxAmbientC: 20, factor: 1.08 },
    { maxAmbientC: 25, factor: 1.04 },
    { maxAmbientC: 30, factor: 1.0 },
    { maxAmbientC: 35, factor: 0.96 },
    { maxAmbientC: 40, factor: 0.91 },
    { maxAmbientC: 45, factor: 0.87 },
    { maxAmbientC: 50, factor: 0.82 },
    { maxAmbientC: 55, factor: 0.76 },
    { maxAmbientC: 60, factor: 0.71 },
    { maxAmbientC: 65, factor: 0.65 },
    { maxAmbientC: 70, factor: 0.58 },
    { maxAmbientC: 75, factor: 0.5 },
    { maxAmbientC: 80, factor: 0.41 },
    { maxAmbientC: 85, factor: 0.29 },
  ],
};

/** Highest ambient temperature (°C) for which each column has a listed factor. */
export const MAX_AMBIENT_C: Record<TempRating, number> = {
  "60C": 55,
  "75C": 70,
  "90C": 85,
};

/**
 * NEC 2023 Table 310.15(C)(1) — adjustment factors for more than three
 * current-carrying conductors in a raceway/cable. Returns the multiplier.
 * 1–3 conductors → 1.00 (no adjustment).
 */
export function bundleAdjustmentFactor(currentCarryingConductors: number): number {
  const n = currentCarryingConductors;
  if (n <= 3) return 1.0;
  if (n <= 6) return 0.8;
  if (n <= 9) return 0.7;
  if (n <= 20) return 0.5;
  if (n <= 30) return 0.45;
  if (n <= 40) return 0.4;
  return 0.35;
}

/** Temperature correction factor for a given ambient temp + column. */
export function tempCorrectionFactor(ambientC: number, rating: TempRating): number | null {
  const bands = TEMP_CORRECTION_310_15_B_1[rating];
  for (const band of bands) {
    if (ambientC <= band.maxAmbientC) return band.factor;
  }
  return null; // above the highest listed ambient → conductor not usable here
}
